import JSZip from "jszip";
import { utils as XlsxUtils, write as writeXlsx } from "xlsx";
import { getANNLLMAnalyzer } from "src/modules/ANNModule/ANNLLMAnalyzer";
import { addServitutPackageToZip } from "src/propertyInfoTemplates/TingbogServitutBatch";
import { selectExportFileName, selectFieldText, selectHeading } from "../store/selectors";
import type { ServitutExportProgress } from "../model/report";
import { isDigitalRow, type ServitutRow } from "../model/row";
import { cutExportPdf, getRowSourcePdfBlob } from "./rowIo";
import { buildUnavailableServitutTxt, shouldExportSourceDocument, toTxtFileName } from "./exportFillers";

export type ServitutExportMode = "excel" | "zip" | "pdf";
export { buildServitutPdfBlob, buildServitutPdfModel } from "./exportPdf";

function uniqueZipName(fileName: string, usedNames: Set<string>): string {
   const trimmed = fileName.trim() || "servitut.pdf";
   if (!usedNames.has(trimmed)) {
      usedNames.add(trimmed);
      return trimmed;
   }
   const match = /^(.*?)(\.[^.]+)?$/.exec(trimmed);
   const baseName = match?.[1] || "servitut";
   const extension = match?.[2] || "";
   let index = 2;
   while (usedNames.has(`${baseName} (${index})${extension}`)) {
      index += 1;
   }
   const uniqueName = `${baseName} (${index})${extension}`;
   usedNames.add(uniqueName);
   return uniqueName;
}

export function buildServitutExcelBlob(
   rows: ServitutRow[],
   columns: number[],
   columnLabels: Record<string, string>,
   options?: { includeAnalysisFields?: boolean; caseDescription?: string },
): Blob {
   const includeAnalysisFields = options?.includeAnalysisFields === true;
   const caseDescription = options?.caseDescription || "";
   const records = rows.map((row) => {
      const record: Record<string, string | number> = {
         "Prioritet": row.landRegistry.priority,
         "Dato-løbenummer": row.identity.datoloebenr || row.landRegistry.displayAktId,
         "Servituttekst": selectHeading(row),
      };
      if (includeAnalysisFields) {
         record["Resume"] = selectFieldText(row, "resume", caseDescription);
         record["Servituttype"] = selectFieldText(row, "condition", caseDescription);
         record["Påtaleberettiget"] = selectFieldText(row, "entitledParty", caseDescription);
         record["Redegørelse"] = selectFieldText(row, "statement", caseDescription);
      }
      columns.forEach((bfe) => {
         record[columnLabels[String(bfe)] || String(bfe)] = row.landRegistry.includedBfes.includes(bfe) ? "x" : "";
      });
      return record;
   });
   const worksheet = XlsxUtils.json_to_sheet(records);
   const workbook = XlsxUtils.book_new();
   XlsxUtils.book_append_sheet(workbook, worksheet, "Servitutredegørelse");
   const buffer = writeXlsx(workbook, { bookType: "xlsx", type: "array" }) as ArrayBuffer;
   return new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
}

export async function buildServitutZip(
   rows: ServitutRow[],
   columns: number[],
   columnLabels: Record<string, string>,
   onProgress?: (state: ServitutExportProgress) => void | Promise<void>,
   options?: { includeAnalysisFields?: boolean; caseDescription?: string },
): Promise<Blob> {
   const zip = new JSZip();
   const usedNames = new Set<string>();
   const analyzer = getANNLLMAnalyzer();
   const totalSteps = 2 + rows.length;
   let completedSteps = 0;
   const emit = async (state: ServitutExportProgress) => {
      await onProgress?.(state);
   };

   await emit({ completedSteps, totalSteps, stage: "spreadsheet", activeRowKey: null });
   zip.file(
      uniqueZipName("Servitutredegørelse.xlsx", usedNames),
      buildServitutExcelBlob(rows, columns, columnLabels, options),
   );
   completedSteps += 1;
   await emit({ completedSteps, totalSteps, stage: "spreadsheet", activeRowKey: null });

   for (let docIndex = 0; docIndex < rows.length; docIndex += 1) {
      const row = rows[docIndex];
      await emit({
         completedSteps,
         totalSteps,
         stage: "document",
         activeRowKey: row.id,
         documentIndex: docIndex + 1,
         documentTotal: rows.length,
      });
      const baseFileName = selectExportFileName(row);
      if (!shouldExportSourceDocument(row)) {
         zip.file(uniqueZipName(toTxtFileName(baseFileName), usedNames), buildUnavailableServitutTxt(row));
         completedSteps += 1;
         continue;
      }
      if (isDigitalRow(row) && row.document.kind !== "upload" && row.document.kind !== "annotation") {
         const electronic = row.document.kind === "tingbog_digital"
            ? row.document.electronicServitut
            : row.registryBinding?.kind === "tingbog_digital"
               ? row.registryBinding.electronicServitut
               : row.landRegistry.servitut;
         if (!electronic) {
            zip.file(
               uniqueZipName(toTxtFileName(baseFileName), usedNames),
               `Digital servitutpakke ikke tilgængelig (${row.landRegistry.displayAktId}).\n${row.landRegistry.summary || ""}`,
            );
         } else {
            try {
               await addServitutPackageToZip(zip, electronic);
            } catch (error) {
               zip.file(
                  uniqueZipName(toTxtFileName(baseFileName), usedNames),
                  `Kunne ikke hente digital servitutpakke: ${error instanceof Error ? error.message : String(error)}\n\n${row.landRegistry.summary || "Ingen servituttekst."}`,
               );
            }
         }
      } else {
         try {
            const pdfBlob = row.document.kind === "annotation" || row.document.kind === "upload"
               ? await getRowSourcePdfBlob(row)
               : (row.document.kind === "tingbog_akt"
                  ? await analyzer.fetchPdf(row.document.aktId)
                  : await getRowSourcePdfBlob(row));
            if (!pdfBlob) {
               throw new Error("PDF mangler");
            }
            zip.file(uniqueZipName(baseFileName, usedNames), await cutExportPdf(row, pdfBlob));
         } catch (error) {
            zip.file(
               uniqueZipName(toTxtFileName(baseFileName), usedNames),
               `Could not find servitut: ${row.landRegistry.displayAktId}\n${error instanceof Error ? error.message : String(error)}\n\n${row.landRegistry.summary || ""}`,
            );
         }
      }
      completedSteps += 1;
   }

   await emit({ completedSteps, totalSteps, stage: "compress", activeRowKey: null });
   const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
   completedSteps += 1;
   await emit({ completedSteps, totalSteps, stage: "compress", activeRowKey: null });
   return blob;
}
